import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js'; // toujours utile pour modals, toasts, collapses, etc.

import '@fortawesome/fontawesome-free/css/all.css';
import '@fortawesome/fontawesome-free/js/all.js';

import 'bootstrap-icons/font/bootstrap-icons.css';

import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'


const app = createApp(App);

/**
 * DIRECTIVE : v-dropdown-fixed
 * -----------------------------------------------------------
 * Implémentation 100% maison, indépendante de la classe Dropdown
 * de Bootstrap et de son système data-api.
 *
 * Pourquoi : le data-api de Bootstrap ([data-bs-toggle="dropdown"])
 * enregistre un écouteur de clic global sur `document`. Si le module
 * Bootstrap est chargé/importé plus d'une fois dans le bundle (ce qui
 * arrivait avec l'import séparé `from 'bootstrap'`), on se retrouve
 * avec DEUX écouteurs concurrents qui s'annulent l'un l'autre : le
 * menu s'ouvre puis se referme aussitôt. Plutôt que de dépendre d'une
 * config d'import fragile, on gère tout nous-mêmes ici :
 *  - un seul écouteur de clic sur le bouton,
 *  - positionnement en `position: fixed` calculé à la main
 *    (jamais coupé par un `.table-responsive` en overflow),
 *  - fermeture au clic extérieur et au scroll.
 *
 * Usage : gardez `data-bs-toggle="dropdown"` pour le style visuel
 * (icône, etc. si besoin), mais AJOUTEZ `v-dropdown-fixed` sur le
 * <button>. Le comportement d'ouverture est entièrement pris en
 * charge par cette directive, plus par Bootstrap.
 */
app.directive('dropdown-fixed', {
  mounted(el) {

    const menu = el.parentElement?.querySelector('.dropdown-menu');
    if (!menu) return;

    // On empêche Bootstrap de gérer ce bouton, pour éviter tout conflit.
    el.removeAttribute('data-bs-toggle');

    const closeMenu = () => {
      menu.classList.remove('show');
      el.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onClickOutside, true);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };

    const onClickOutside = (e) => {
      if (!menu.contains(e.target) && e.target !== el) {
        closeMenu();
      }
    };

    const openMenu = () => {
      // Ferme tout autre dropdown déjà ouvert sur la page
      document.querySelectorAll('.dropdown-menu.show').forEach(m => {
        if (m !== menu) m.classList.remove('show');
      });

      const rect = el.getBoundingClientRect();

      menu.style.position = 'fixed';
      menu.style.top = `${rect.bottom + 2}px`;
      menu.style.margin = '0';

      // Aligne à droite si le menu déborderait de l'écran
      const menuWidth = menu.offsetWidth || 180;
      const overflowsRight = rect.left + menuWidth > window.innerWidth;

      if (overflowsRight) {
        menu.style.left = 'auto';
        menu.style.right = `${window.innerWidth - rect.right}px`;
      } else {
        menu.style.left = `${rect.left}px`;
        menu.style.right = 'auto';
      }

      menu.classList.add('show');
      el.setAttribute('aria-expanded', 'true');

      document.addEventListener('click', onClickOutside, true);
      window.addEventListener('scroll', closeMenu, true);
      window.addEventListener('resize', closeMenu);
    };

    const onToggleClick = (e) => {
      e.stopPropagation();
      if (menu.classList.contains('show')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    el.addEventListener('click', onToggleClick);

    // Nettoyage stocké sur l'élément pour beforeUnmount
    el._dropdownFixedCleanup = () => {
      el.removeEventListener('click', onToggleClick);
      document.removeEventListener('click', onClickOutside, true);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  },

  beforeUnmount(el) {
    if (el._dropdownFixedCleanup) {
      el._dropdownFixedCleanup();
    }
  }
});

app
  .use(store)
  .use(router)
  .component('Multiselect', Multiselect)
  .mount('#app')